import { ReactNode, useCallback, useEffect, useMemo } from 'react';

import { useGetDistinctInventoryTypesBySite, useGetSiteInventoryHealth } from '@restDataAccess';

import { useConfigStore, useSelectedSiteStore } from '@/stores';

import { useAccessibleSitesContext } from './AccessibleSitesContext.ts';
import {
  isSelectedSiteIdInTypesBySite,
  resolveAppCurrentSite,
  shouldRequireSiteSelection,
} from './siteProvider.functions.ts';
import { AvailableServicesProvider } from './siteProvider/AvailableServicesProvider.tsx';
import { InventoryDataProvider } from './siteProvider/InventoryDataProvider.tsx';
import { SiteContext, type SiteContextType } from './siteProvider/SiteContext.ts';

type SiteProviderProps = {
  children: ReactNode;
  persist?: boolean;
  siteId?: string;
};

/**
 * Binds a subtree to one site. Must be mounted under {@link AccessibleSitesProvider}, which owns
 * the accessible-site list and the `/config` gate.
 */
export const SiteProvider = ({ children, persist = false, siteId }: SiteProviderProps) => {
  const defaultSite = useConfigStore(s => s.defaultSite);
  const portableMode = useConfigStore(s => s.portableMode);
  const selectedSiteId = useSelectedSiteStore(s => s.selectedSiteId);
  const setSelectedSiteId = useSelectedSiteStore(s => s.setSelectedSiteId);
  const { sites, areSitesResolved } = useAccessibleSitesContext();
  const { data: typesBySite, isSuccess: areTypesBySiteFetched } =
    useGetDistinctInventoryTypesBySite(Boolean(portableMode) && !persist);

  const getSiteApis = useCallback(
    (id: string) => {
      if (!sites.length) return defaultSite;

      return sites.find(site => site.id === id) || defaultSite;
    },
    [defaultSite, sites]
  );
  // Nested providers (`persist`) are pinned to an explicit site — a layout widget, a config panel or
  // a route subtree. The app-level provider instead follows the site picked in the top bar. In
  // portable mode a stored id that is not a `getDistinctTypesBySite` key is treated as empty.
  const currentSite = useMemo(
    () =>
      resolveAppCurrentSite({
        persist,
        persistSiteId: siteId,
        getPersistedSite: getSiteApis,
        portableMode: Boolean(portableMode),
        selectedSiteId,
        typesBySite,
        areTypesBySiteFetched,
        sites,
        defaultSite,
      }),
    [
      areTypesBySiteFetched,
      defaultSite,
      getSiteApis,
      persist,
      portableMode,
      selectedSiteId,
      siteId,
      sites,
      typesBySite,
    ]
  );
  const health = useGetSiteInventoryHealth(currentSite, !persist);
  const mustSelectSite = shouldRequireSiteSelection({
    persist,
    portableMode: Boolean(portableMode),
    areSitesResolved,
    sites,
    currentSite,
  });

  useEffect(() => {
    if (persist || !portableMode || !areTypesBySiteFetched) return;
    if (isSelectedSiteIdInTypesBySite(selectedSiteId, typesBySite)) return;
    if (selectedSiteId == null) return;
    setSelectedSiteId(undefined);
  }, [
    areTypesBySiteFetched,
    persist,
    portableMode,
    selectedSiteId,
    setSelectedSiteId,
    typesBySite,
  ]);

  const value: SiteContextType = useMemo(
    () => ({
      areSitesResolved,
      currentSite,
      health,
      mustSelectSite,
      sites,
    }),
    [sites, currentSite, areSitesResolved, health, mustSelectSite]
  );

  return (
    <SiteContext.Provider value={value}>
      <AvailableServicesProvider>
        <InventoryDataProvider>{children}</InventoryDataProvider>
      </AvailableServicesProvider>
    </SiteContext.Provider>
  );
};
